// Import react.
import * as React from 'react';
// Import app config.
import AppConfig from '@config/app';
// Import services.
import {
	AuthenticationService,
} from '@app/services';
// Import interfaces.
import {
	ISessionTimeLeft,
} from '@app/interfaces';
// Import types.
import {
	TSessionTimeListenerCallback,
} from '@app/types';

interface UseSessionTimeLeftParameters {
	initiateSessionTimeCheckOnMount?: boolean;
	onSessionExpired: () => void;
}

interface UseSessionTimeLeftResult {
	initiateSessionTimeCheck: () => void;
	stopSessionTimeCheck: () => void;
	registerSessionTimeListener: (sessionTimeListener: TSessionTimeListenerCallback) => void;
}

const useSessionTimeLeft = (params: UseSessionTimeLeftParameters): UseSessionTimeLeftResult => {

	// #region exported methods.

	const initiateSessionTimeCheck = (): void => {
		startTimer();
	}

	const stopSessionTimeCheck = (): void => {
		clearTimer();
	}

	const registerSessionTimeListener = (sessionTimeListener: TSessionTimeListenerCallback): void => {
		sessionTimeListenersRef.current = [...sessionTimeListenersRef.current, sessionTimeListener];
	}

	// #endregion exported methods.

	// #region refs

	const timeoutInstanceRef = React.useRef<NodeJS.Timeout | undefined>(undefined);
	const sessionTimeListenersRef = React.useRef<TSessionTimeListenerCallback[]>([]);
	const isUnmounted = React.useRef(false);

	// #endregion refs

	// #region variables

	let sessionExpiresInSeconds: number = AppConfig.session_expires_in_seconds;

	const initiateSessionTimeCheckOnMount: boolean = React.useMemo(() => {
		return params.initiateSessionTimeCheckOnMount ?? false
	}, [params.initiateSessionTimeCheckOnMount]);

	// #endregion variables

	// #region life-cycle methods.

	React.useEffect(() => {
		if (initiateSessionTimeCheckOnMount) {
			startTimer();
		}
		return () => {
			isUnmounted.current = true;
			clearTimer();
		}
	}, []);

	// #endregion life-cycle methods.

	// #region member methods.

	const resetTimer = (): void => {
		clearTimer();
		startTimer();
	}

	const startTimer = (): void => {
		timeoutInstanceRef.current = setTimeout(getSessionTimeLeft, 1000);
	}

	const clearTimer = (): void => {
		if (timeoutInstanceRef.current !== undefined) {
			clearTimeout(timeoutInstanceRef.current);
			timeoutInstanceRef.current = undefined;
		}
	}

	const isSessionAboutToExpire = (sessionTimeLeft: ISessionTimeLeft): boolean => {
		return sessionTimeLeft.totalSecondsLeft < sessionExpiresInSeconds
			&& !AuthenticationService.getInstance().isSessionExpired();
	}

	const invokeSessionTimeListeners = (sessionTimeLeft: ISessionTimeLeft): void => {
		let isSessionAboutToExpireResult: boolean = isSessionAboutToExpire(sessionTimeLeft);
		sessionTimeListenersRef.current.forEach(sessionTimeListener => sessionTimeListener(sessionTimeLeft, isSessionAboutToExpireResult));
	}

	const getSessionTimeLeft = async (): Promise<void> => {
		if (AuthenticationService.getInstance().isLoggedIn()) {
			if (AuthenticationService.getInstance().isSessionExpired()) {
				//If we are here it means the popup has timed-out and we need to log out the user.
				params.onSessionExpired();
			}
			else {
				//If we are here, we are updating the time left shown to the user.
				let minutesLeft: number = AuthenticationService.getInstance().getRemainingTokenLifetimeInMinutes();
				let secondsLeft: number = AuthenticationService.getInstance().getRemainingTokenLifetimeInSeconds();
				let totalSecondsLeft: number = AuthenticationService.getInstance().getTokenLifetimeInSeconds();

				if (!isUnmounted.current) {
					invokeSessionTimeListeners({
						sessionTimeMinutesLeft: minutesLeft,
						sessionTimeSecondsLeft: secondsLeft,
						totalSecondsLeft: totalSecondsLeft,
					});
					resetTimer();
				}
			}
		}
		else {
			if (!isUnmounted.current) {
				invokeSessionTimeListeners({
					sessionTimeMinutesLeft: 0,
					sessionTimeSecondsLeft: 0,
					totalSecondsLeft: 0,
				});
				resetTimer();
			}
		}
	}

	// #endregion member methods.

	return {
		initiateSessionTimeCheck,
		stopSessionTimeCheck,
		registerSessionTimeListener,
	};
}

// Export parameters.
export {
	UseSessionTimeLeftParameters,
};

// Export results.
export {
	UseSessionTimeLeftResult,
};

export default useSessionTimeLeft;